import React, { useMemo } from 'react';
import { Alert, InputNumber, Segmented, Select, Space, Switch } from 'antd';

import {
	ECPERCENT_RANGE,
	MODULE_CEILING,
	SHAPES,
	SYMBOLOGIES,
	SYMBOLOGY_GROUPS,
	eclevelsOf,
	masksOf,
	moduleFloor,
	versionsOf,
} from '../../utils/barcode/symbologies';
import TokenInput from '../common/TokenInput';
import { elementLabel } from '../items/itemValueDisplay';

// The record's own four tokens, in plain words. The stored names stay the
// API's (`_url`, `_id`, `_collection`, `_card`); only the menu and the pill
// speak differently.
export const RECORD_TOKEN_NODES = [
	{ token: '{{_url}}', label: "This item's link" },
	{ token: '{{_id}}', label: "This item's ID" },
	{ token: '{{_collection}}', label: 'Its collection' },
	{ token: '{{_card}}', label: 'Its card' },
];

/** The menu a barcode's content offers: the record tokens, then the card's elements. */
export function buildBarcodeTokens(elementDefs = []) {
	const own = (elementDefs || [])
		.filter(def => def?.name && def.dataType !== 'file' && def.dataType !== 'repeating-group')
		.map(def => ({ token: `{{${def.name}}}`, label: elementLabel(def) }));
	return [...RECORD_TOKEN_NODES, ...own];
}

/**
 * The barcode block's side panel.
 *
 * The fields an author sees are exactly the ones their symbology reads —
 * driven from the registry's `code` list, not from a big switch — because
 * the API refuses a key that belongs to a different symbology, and a field
 * that cannot be saved is worse than a field that is not there.
 *
 * THE X-DIMENSION IS THE FIELD THAT MATTERS and it is spelled in the page's
 * own unit, never as a scale. The floor beneath it is the print floor, and
 * the panel says what the floor is rather than silently clamping: an author
 * who typed 0.004in wanted a smaller label, and the honest answer is that no
 * printer can make that symbol readable.
 */

// Roughly what each letter recovers. WHICH of them a symbology offers is the
// registry's call, not this list's: Micro QR has no H and rMQR has only M
// and H, and offering one the encoder cannot find a symbol for reads as a
// payload problem to an author who does not have one.
const ECLEVEL_LABELS = { L: 'L — 7%', M: 'M — 15%', Q: 'Q — 25%', H: 'H — 30%' };

export default function BarcodeInspector({ block, unit, elementDefs, onProps, onCode }) {
	const spec = SYMBOLOGIES[block.symbology];
	const owns = key => !!spec && spec.code.includes(key);
	const code = block.code || {};
	const versions = versionsOf(block.symbology);
	const eclevels = eclevelsOf(block.symbology);
	const masks = masksOf(block.symbology);
	const floor = moduleFloor(block.symbology, unit);
	const ceiling = MODULE_CEILING[unit === 'mm' ? 'mm' : 'in'];
	const belowFloor = typeof block.module === 'number' && block.module < floor;

	const tokens = useMemo(() => buildBarcodeTokens(elementDefs), [elementDefs]);

	const set = (key, value) => onProps({ [key]: value });
	const setCode = (key, value) => onCode({
		...code,
		...(value === undefined || value === null || value === '' ? { [key]: undefined } : { [key]: value }),
	});

	return (
		<>
			<Select
				value={block.symbology}
				onChange={value => set('symbology', value)}
				aria-label="Symbology"
				options={SYMBOLOGY_GROUPS.map(group => ({ label: group.label, options: group.options }))}
				style={{ width: '100%' }}
			/>
			{spec?.note ? <p className="form-panel__hint">{spec.note}</p> : null}
			{spec && spec.zxing === null ? (
				<Alert
					type="warning"
					showIcon
					message="This prints but cannot be scanned back"
					description="Neither the phone camera nor the reader we ship knows MSI. Print it for an existing shelf system; do not print it for keepr to read."
				/>
			) : null}

			<TokenInput
				multiline
				value={block.payload || ''}
				tokens={tokens}
				onChange={value => set('payload', value)}
				placeholder="Choose what the code holds"
				ariaLabel="Barcode payload"
				describedBy="barcode-payload-hint"
			/>
			<p className="form-panel__hint" id="barcode-payload-hint">
				What the code holds. Add this item&rsquo;s link, its ID or any of its elements from the menu.{' '}
				{spec?.gs1
					? 'This symbology reads GS1 application identifiers, so write the (01)(17) skeleton yourself. Values you add are escaped for you.'
					: 'A value that cannot be encoded shows as an error on this block alone; the rest of the sheet still prints.'}
			</p>

			<Space wrap>
				<InputNumber
					value={block.module}
					onChange={value => set('module', value)}
					aria-label="Module width"
					addonBefore="X-dim"
					addonAfter={unit}
					step={unit === 'mm' ? 0.05 : 0.001}
					min={0}
					max={ceiling}
					status={belowFloor ? 'error' : undefined}
				/>
				<InputNumber
					value={block.quiet}
					onChange={value => set('quiet', value)}
					aria-label="Quiet zone"
					addonBefore="Quiet"
					addonAfter="mod"
					step={1}
					min={0}
					max={64}
				/>
			</Space>
			{belowFloor ? (
				<Alert
					type="error"
					showIcon
					message={`A module under ${floor}${unit} will not print`}
					description="The narrow bars round away to nothing at the printer, and the symbol is unreadable. This layout will be refused when you save it."
				/>
			) : (
				<p className="form-panel__hint">
					One module is one narrow bar or one cell — the whole symbol is that many modules wide. The floor for this
					symbology is {floor}{unit}. Printed size is true size only at 100% scale with &ldquo;fit to page&rdquo; off.
				</p>
			)}

			<Segmented
				block
				value={block.rotate || 'N'}
				onChange={value => set('rotate', value)}
				options={[
					{ value: 'N', label: 'Up' },
					{ value: 'R', label: 'Right' },
					{ value: 'I', label: 'Down' },
					{ value: 'L', label: 'Left' },
				]}
			/>

			{owns('text') ? (
				<Segmented
					block
					value={code.text || 'none'}
					onChange={value => setCode('text', value === 'none' ? undefined : value)}
					options={[
						{ value: 'none', label: 'No text' },
						{ value: 'below', label: 'Text below' },
						{ value: 'above', label: 'Text above' },
					]}
				/>
			) : null}

			<Space wrap>
				{owns('eclevel') ? (
					<Select
						value={code.eclevel}
						onChange={value => setCode('eclevel', value)}
						aria-label="Error correction"
						placeholder="Correction"
						allowClear
						options={eclevels.map(value => ({ value, label: ECLEVEL_LABELS[value] }))}
						style={{ minWidth: 130 }}
					/>
				) : null}
				{owns('ecpercent') ? (
					<InputNumber
						value={code.ecpercent}
						onChange={value => setCode('ecpercent', value)}
						aria-label="Error correction"
						addonBefore="EC"
						addonAfter="%"
						min={ECPERCENT_RANGE.min}
						max={ECPERCENT_RANGE.max}
					/>
				) : null}
				{owns('eclevel417') ? (
					<InputNumber
						value={code.eclevel417}
						onChange={value => setCode('eclevel417', value)}
						aria-label="Error correction level"
						addonBefore="EC"
						min={0}
						max={8}
					/>
				) : null}
				{owns('shape') ? (
					<Select
						value={code.shape}
						onChange={value => setCode('shape', value === 'auto' ? undefined : value)}
						aria-label="Symbol shape"
						placeholder="Shape"
						allowClear
						options={SHAPES.map(value => ({ value, label: value }))}
						style={{ minWidth: 130 }}
					/>
				) : null}
				{owns('checkdigit') ? (
					<Select
						value={code.checkdigit}
						onChange={value => setCode('checkdigit', value === 'none' ? undefined : value)}
						aria-label="Check digit"
						placeholder="Check digit"
						allowClear
						options={(spec.checkdigits || ['none']).map(value => ({ value, label: value }))}
						style={{ minWidth: 130 }}
					/>
				) : null}
				{/* A version is a different VOCABULARY per symbology — 1-40 for a
				    full QR, M1-M4 for a Micro QR, R{rows}x{cols} for an rMQR,
				    an RxC size for a Data Matrix. The control follows the
				    registry's descriptor rather than offering an integer the
				    encoder would throw on. */}
				{owns('version') && versions?.kind === 'int' ? (
					<InputNumber
						value={code.version}
						onChange={value => setCode('version', value)}
						aria-label="Version"
						addonBefore="Ver"
						min={versions.min}
						max={versions.max}
					/>
				) : null}
				{owns('version') && versions?.kind === 'enum' ? (
					<Select
						value={code.version}
						onChange={value => setCode('version', value)}
						aria-label="Version"
						placeholder={spec.autoVersion ? 'Size (auto)' : 'Size'}
						allowClear
						showSearch
						options={versions.values.map(value => ({ value, label: value }))}
						style={{ minWidth: 140 }}
					/>
				) : null}
				{owns('mask') ? (
					<InputNumber
						value={code.mask}
						onChange={value => setCode('mask', value)}
						aria-label="Mask"
						addonBefore="Mask"
						min={masks.min}
						max={masks.max}
					/>
				) : null}
				{owns('layers') ? (
					<InputNumber
						value={code.layers}
						onChange={value => setCode('layers', value)}
						aria-label="Layers"
						addonBefore="Layers"
						min={1}
						max={32}
					/>
				) : null}
				{owns('columns') ? (
					<InputNumber
						value={code.columns}
						onChange={value => setCode('columns', value)}
						aria-label="Columns"
						addonBefore="Cols"
						min={1}
						max={30}
					/>
				) : null}
				{owns('rows') ? (
					<InputNumber
						value={code.rows}
						onChange={value => setCode('rows', value)}
						aria-label="Rows"
						addonBefore="Rows"
						min={1}
						max={90}
					/>
				) : null}
				{owns('compact') ? (
					<Space>
						<Switch
							checked={code.compact === true}
							onChange={value => setCode('compact', value ? true : undefined)}
							id="barcode-compact"
						/>
						<label htmlFor="barcode-compact">Compact PDF417</label>
					</Space>
				) : null}
			</Space>
		</>
	);
}
